import { Button } from '@/components/ui/button';
import { useCompany } from '@/hooks/use-company';
import { usePushNotifications } from '@/hooks/use-push-notifications';
import { getApiErrorMessage } from '@/lib/api/error';
import { cn } from '@/lib/utils';
import { notificationPreferencesQueryKey, UsersService } from '@/services/users.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, BellRing, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

interface PushNotificationPromptProps {
  className?: string;
}

export function PushNotificationPrompt({ className }: PushNotificationPromptProps) {
  const { activeCompany } = useCompany();
  const { status, isPending, enable, disable } = usePushNotifications();
  const queryClient = useQueryClient();
  const queryKey = notificationPreferencesQueryKey(activeCompany?.id);
  const preferencesQuery = useQuery({
    queryKey,
    queryFn: UsersService.getNotificationPreferences,
    enabled: Boolean(activeCompany?.id),
  });

  if (status === 'unsupported') return null;

  const handleEnable = async () => {
    try {
      if (await enable()) {
        const preferences = await UsersService.updateNotificationPreferences({ pushEnabled: true });
        queryClient.setQueryData(queryKey, preferences);
        toast.success('Notificações em tempo real ativadas');
      }
    } catch (error) {
      toast.error('Não foi possível ativar as notificações', {
        description: getApiErrorMessage(error, 'Confira as permissões do navegador e tente novamente.'),
      });
    }
  };

  const handleDisable = async () => {
    try {
      const preferences = await UsersService.updateNotificationPreferences({ pushEnabled: false });
      queryClient.setQueryData(queryKey, preferences);
      await disable();
      toast.success('Notificações em tempo real desativadas neste dispositivo');
    } catch (error) {
      toast.error('Não foi possível desativar as notificações', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    }
  };

  if (status === 'subscribed' && preferencesQuery.data?.pushEnabled !== false) {
    return (
      <div className={cn('flex items-center gap-3 bg-emerald-500/8 px-5 py-3', className)}>
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
        <p className="min-w-0 flex-1 text-sm font-medium text-emerald-700 dark:text-emerald-400">
          Push ativado neste dispositivo
        </p>
        <Button variant="ghost" size="sm" disabled={isPending} onClick={() => void handleDisable()}>
          Desativar
        </Button>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className={cn('bg-destructive/8 flex gap-3 px-5 py-4', className)}>
        <BellOff className="text-destructive mt-0.5 h-5 w-5 shrink-0" />
        <div>
          <p className="text-sm font-semibold">Notificações bloqueadas</p>
          <p className="text-muted-foreground mt-1 text-xs leading-5">
            Libere as notificações nas configurações deste navegador para receber novos agendamentos.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bg-primary/5 flex gap-3 px-5 py-4', className)}>
      <BellRing className="text-primary mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">Receba novos agendamentos na hora</p>
        <p className="text-muted-foreground mt-1 text-xs leading-5">
          Ative o Push para ser avisado mesmo quando o Vira Tempo estiver fechado.
        </p>
        <Button size="sm" className="mt-3" disabled={isPending} onClick={() => void handleEnable()}>
          {isPending ? 'Ativando…' : 'Ativar notificações'}
        </Button>
      </div>
    </div>
  );
}
